// Enhanced Search Redesign - Full Width Implementation
class SearchRedesign extends HTMLElement {
  constructor() {
    super();
    
    this.searchStates = {
      INITIAL: 'initial',
      TYPING: 'typing',
      RESULTS: 'results'
    };
    
    this.currentState = this.searchStates.INITIAL;
    this.searchTimeout = null;
    this.saveToRecentTimeout = null;
    this.searchAbortController = null;
    
    // Store recent searches in localStorage
    this.recentSearches = this.getRecentSearches();
    
    // Get trending searches from theme settings
    this.trendingSearches = this.getTrendingSearches();
    
    // Initialize search results storage
    this.searchCollections = [];
    this.resultPages = [];
  }
  
  connectedCallback() {
    this.initializeSearch();
    this.hijackSearchButtons();
    
    // Add method to window for debugging
    window.clearRecentSearches = () => {
      localStorage.removeItem('recentSearches');
      this.recentSearches = [];
      this.updatePanel();
      console.log('Recent searches cleared');
    };
  }
  
  disconnectedCallback() {
    // Clean up observers
    if (this.announcementObserver) {
      this.announcementObserver.disconnect();
    }
  }
  
  hijackSearchButtons() {
    // Intercept clicks on search buttons/links
    document.addEventListener('click', (e) => {
      const searchLink = e.target.closest('.show-search-link, [href*="/search"], [data-search-trigger]');
      
      if (searchLink && searchLink.getAttribute('href')?.includes('/search')) {
        e.preventDefault();
        this.handleFocus();
      }
    });
    
    // Also intercept the body class change that would show old search
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.target === document.body && mutation.attributeName === 'class') {
          if (document.body.classList.contains('show-search')) {
            document.body.classList.remove('show-search');
            this.handleFocus();
          }
        }
      });
    });
    
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });
  }
  
  initializeSearch() {
    // Calculate announcement bar height
    this.calculateAnnouncementBarHeight();
    
    // Create the search structure
    this.innerHTML = `
      <div class="search-redesign__container">
        <div class="search-redesign__bar">
          <div class="search-redesign__input-wrapper">
            <input 
              type="text" 
              class="search-redesign__input" 
              placeholder="${theme.strings.searchPlaceholder || 'Search'}"
              aria-label="Search"
              autocomplete="off"
            />
            <div class="search-redesign__autocomplete-hint"></div>
            <button class="search-redesign__submit" type="submit" aria-label="Search">
              ${this.renderSearchIcon()}
            </button>
            <button class="search-redesign__close" aria-label="Close search">
              ${this.renderCloseIcon()}
            </button>
          </div>
        </div>
        
        <div class="search-redesign__content">
          <aside class="search-redesign__panel">
            <div class="search-redesign__panel-content"></div>
          </aside>
          
          <main class="search-redesign__results">
            <div class="search-redesign__results-content"></div>
          </main>
        </div>
      </div>
    `;
    
    // Get elements
    this.input = this.querySelector('.search-redesign__input');
    this.hint = this.querySelector('.search-redesign__autocomplete-hint');
    this.panel = this.querySelector('.search-redesign__panel-content');
    this.results = this.querySelector('.search-redesign__results-content');
    this.closeBtn = this.querySelector('.search-redesign__close');
    this.submitBtn = this.querySelector('.search-redesign__submit');
    
    // Debug close button
    if (!this.closeBtn) {
      console.error('Close button not found');
    }
    
    // Bind events
    this.bindEvents();
    
    // Set initial state
    this.updateState(this.searchStates.INITIAL);
  }
  
  bindEvents() {
    // Input events
    this.input.addEventListener('input', this.handleInput.bind(this));
    this.input.addEventListener('focus', this.handleFocus.bind(this));
    this.input.addEventListener('keydown', this.handleKeyDown.bind(this));
    
    // Close button
    this.closeBtn.addEventListener('click', this.handleClose.bind(this));
    
    // Submit button
    this.submitBtn.addEventListener('click', this.handleSubmit.bind(this));
    
    // Form submission
    const form = this.closest('form');
    if (form) {
      form.addEventListener('submit', this.handleSubmit.bind(this));
    }
    
    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('search-redesign--active')) {
        this.handleClose();
      }
    });
  }
  
  handleKeyDown(e) {
    // Handle TAB key for autocomplete
    if (e.key === 'Tab' && this.currentSuggestion) {
      e.preventDefault();
      this.input.value = this.currentSuggestion;
      this.updateHint('');
      this.handleInput(e);
    }
  }
  
  handleInput(e) {
    const value = e.target.value.trim();
    
    // Clear existing timeouts
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    if (this.saveToRecentTimeout) {
      clearTimeout(this.saveToRecentTimeout);
    }
    
    if (value.length > 0) {
      this.updateState(this.searchStates.TYPING);
      
      // Fetch suggestions immediately for the left panel
      this.fetchDynamicSuggestions(value);
      
      // Debounce search
      this.searchTimeout = setTimeout(() => {
        this.performSearch(value);
      }, 300);
      
      // Save to recent searches after 2 seconds of inactivity
      this.saveToRecentTimeout = setTimeout(() => {
        if (value && this.currentState === this.searchStates.RESULTS) {
          this.addToRecentSearches(value);
        }
      }, 2000);
    } else {
      // Return to initial state and reload default products
      this.updateState(this.searchStates.INITIAL);
      this.updateHint('');
      this.currentSuggestion = null;
      this.results.innerHTML = '';
      this.defaultProductsLoaded = false;
      this.loadDefaultProducts();
    }
  }
  
  async fetchDynamicSuggestions(query) {
    try {
      // First, try to get HTML suggestions from the search-suggestions section
      const htmlUrl = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(query)}&section_id=search-suggestions`;
      
      const htmlResponse = await fetch(htmlUrl);
      if (htmlResponse.ok) {
        const html = await htmlResponse.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Extract query suggestions from the HTML response
        const querySuggestions = doc.querySelectorAll('.search-suggestions__list-item a');
        
        if (querySuggestions.length > 0) {
          this.shopifyQuerySuggestions = [];
          this.dynamicSuggestions = [];
          
          querySuggestions.forEach(link => {
            const styledText = link.innerHTML;
            const plainText = link.textContent.trim();
            
            this.shopifyQuerySuggestions.push({
              styledText: styledText,
              plainText: plainText
            });
            
            this.dynamicSuggestions.push(plainText);
          });
          
          // Update ghost text with first suggestion
          this.updateGhostTextFromSuggestions(query);
          this.updatePanel();
          return;
        }
      }
      
      // Fallback to JSON API
      const searchUrl = new URL(theme.routes.base + theme.routes.predictiveSearch);
      searchUrl.searchParams.set('q', query);
      searchUrl.searchParams.set('resources[type]', 'query');
      searchUrl.searchParams.set('resources[limit]', '10');
      
      const response = await fetch(searchUrl);
      if (!response.ok) {
        return;
      }
      
      const data = await response.json();
      
      // Store the raw query suggestions from Shopify Search & Discovery
      this.shopifyQuerySuggestions = [];
      this.dynamicSuggestions = [];
      
      // Extract query suggestions from Shopify (these are the autocomplete/ghost text suggestions)
      if (data.resources?.results?.queries) {
        this.shopifyQuerySuggestions = data.resources.results.queries.map(query => {
          // Store both the styled text and plain text
          return {
            styledText: query.styled_text || query.text,
            plainText: query.text || this.extractPlainText(query.styled_text)
          };
        });
        
        // Add plain text versions to dynamic suggestions
        this.shopifyQuerySuggestions.forEach(suggestion => {
          if (suggestion.plainText) {
            this.dynamicSuggestions.push(suggestion.plainText);
          }
        });
      }
      
      // Only add product/collection titles if we have fewer than 5 query suggestions
      if (this.dynamicSuggestions.length < 5) {
        // Add product titles
        if (data.resources?.results?.products) {
          data.resources.results.products.forEach(product => {
            if (product.title && this.dynamicSuggestions.length < 8) {
              this.dynamicSuggestions.push(product.title);
            }
          });
        }
        
        // Add collection titles  
        if (data.resources?.results?.collections) {
          data.resources.results.collections.forEach(collection => {
            if (collection.title && this.dynamicSuggestions.length < 10) {
              this.dynamicSuggestions.push(collection.title);
            }
          });
        }
      }
      
      // Remove duplicates
      this.dynamicSuggestions = [...new Set(this.dynamicSuggestions)];
      
      // Update ghost text with first suggestion
      this.updateGhostTextFromSuggestions(query);
      
      // Update the panel with new suggestions
      this.updatePanel();
    } catch (error) {
      console.warn('Failed to fetch suggestions:', error);
    }
  }
  
  extractPlainText(styledText) {
    // Remove HTML tags from styled text to get plain text
    if (!styledText) return '';
    const temp = document.createElement('div');
    temp.innerHTML = styledText;
    return temp.textContent || temp.innerText || '';
  }
  
  updateGhostTextFromSuggestions(query) {
    if (!query || this.dynamicSuggestions.length === 0) {
      this.updateHint('');
      this.currentSuggestion = null;
      return;
    }
    
    // Find the first suggestion that starts with the query
    const lowerQuery = query.toLowerCase();
    const suggestion = this.dynamicSuggestions.find(s => 
      s.toLowerCase().startsWith(lowerQuery)
    );
    
    if (suggestion) {
      // Pass the full suggestion to updateHint
      this.currentSuggestion = suggestion;
      this.updateHint(suggestion);
    } else {
      this.updateHint('');
      this.currentSuggestion = null;
    }
  }
  
  updateHint(text) {
    if (this.input && this.hint) {
      const userInput = this.input.value;
      
      if (text && userInput && text.toLowerCase().startsWith(userInput.toLowerCase()) && text.length > userInput.length) {
        // Show autocomplete hint below input
        this.hint.innerHTML = `Press <kbd>TAB</kbd> to complete: <span class="suggestion">${text}</span>`;
        this.hint.style.display = 'block';
      } else {
        this.hint.innerHTML = '';
        this.hint.style.display = 'none';
      }
    }
  }
  
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
  
  handleFocus() {
    // Recalculate announcement bar height when opening search
    this.calculateAnnouncementBarHeight();
    this.classList.add('search-redesign--active');
    document.body.classList.add('search-active');
    
    // Focus the input field after a brief delay to ensure panel is visible
    if (this.input) {
      setTimeout(() => {
        this.input.focus();
      }, 100);
    }
    
    // Refresh recent searches from localStorage when opening
    this.recentSearches = this.getRecentSearches();
    
    // Update the panel to show fresh recent searches
    if (this.currentState === this.searchStates.INITIAL) {
      this.updatePanel();
    }
    
    // Load default products if not already loaded and no search performed
    if (!this.defaultProductsLoaded && !this.input.value) {
      this.loadDefaultProducts();
    }
  }
  
  async loadDefaultProducts() {
    try {
      this.defaultProductsLoaded = true;
      
      // Show loading state
      this.results.innerHTML = '<div class="search-redesign__loading">Loading featured products...</div>';
      
      // Get default collection and limit from settings
      const collectionHandle = window.theme?.settings?.searchDefaultCollection || 'best-sellers';
      const productLimit = window.theme?.settings?.searchDefaultProductsLimit || '8';
      
      const searchUrl = new URL(theme.routes.base + `/collections/${collectionHandle}`);
      searchUrl.searchParams.set('section_id', 'main-collection');
      
      const response = await fetch(searchUrl);
      if (!response.ok) throw new Error('Failed to load default products');
      
      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Extract product grid
      const productGrid = doc.querySelector('.collection-listing .product-grid');
      
      if (productGrid && productGrid.children.length > 0) {
        const gridClasses = productGrid.className;
        let products = Array.from(productGrid.children);
        
        // Apply limit if not "all"
        if (productLimit !== 'all') {
          const limit = parseInt(productLimit, 10);
          products = products.slice(0, limit);
        }
        
        this.results.innerHTML = `
          <div class="search-redesign__header">
            <p class="search-redesign__count">Featured Products</p>
          </div>
          <div class="collection-listing">
            <div class="${gridClasses}">
              ${products.map(product => product.outerHTML).join('')}
            </div>
          </div>
        `;
        
        // Initialize any product block scripts
        if (window.theme?.initProductBlocks) {
          window.theme.initProductBlocks(this.results);
        }
      }
    } catch (error) {
      console.warn('Failed to load default products:', error);
      this.results.innerHTML = '';
    }
  }
  
  calculateAnnouncementBarHeight() {
    const announcementBar = document.querySelector('.announcement-bar');
    if (announcementBar) {
      // Check if announcement bar is actually visible and has content
      const isVisible = announcementBar.offsetHeight > 0 && 
                       announcementBar.clientHeight > 0 &&
                       window.getComputedStyle(announcementBar).display !== 'none' &&
                       announcementBar.textContent.trim().length > 0;
      
      const height = isVisible ? announcementBar.offsetHeight : 0;
      this.style.setProperty('--announcement-bar-height', `${height}px`);
      
      // Also observe for changes in case announcement bar changes
      if (!this.announcementObserver && isVisible) {
        this.announcementObserver = new ResizeObserver(() => {
          const newHeight = announcementBar.offsetHeight;
          const hasContent = announcementBar.textContent.trim().length > 0;
          this.style.setProperty('--announcement-bar-height', hasContent ? `${newHeight}px` : '0px');
        });
        this.announcementObserver.observe(announcementBar);
      }
    } else {
      this.style.setProperty('--announcement-bar-height', '0px');
    }
  }
  
  handleClose() {
    this.classList.remove('search-redesign--active');
    document.body.classList.remove('search-active');
    this.input.value = '';
    this.updateHint('');
    this.currentSuggestion = null;
    this.updateState(this.searchStates.INITIAL);
    this.defaultProductsLoaded = false;
    this.results.innerHTML = '';
  }
  
  handleSubmit(e) {
    e.preventDefault();
    const value = this.input.value.trim();
    if (value) {
      this.performSearch(value, true); // Save to recent searches on explicit submit
    }
  }
  
  updateState(state) {
    this.currentState = state;
    this.updatePanel();
    
    // Update container classes
    this.classList.remove('search-redesign--initial', 'search-redesign--typing', 'search-redesign--results');
    this.classList.add(`search-redesign--${state}`);
  }
  
  updatePanel() {
    let content = '';
    
    switch (this.currentState) {
      case this.searchStates.INITIAL:
        content = this.renderInitialPanel();
        break;
      case this.searchStates.TYPING:
        content = this.renderTypingPanel();
        break;
      case this.searchStates.RESULTS:
        content = this.renderResultsPanel();
        break;
    }
    
    this.panel.innerHTML = content;
    this.bindPanelEvents();
  }
  
  renderInitialPanel() {
    // Check if we should fetch popular searches from Shopify
    if (!this.popularSearchesFetched) {
      this.fetchPopularSearches();
    }
    
    // Use Shopify popular searches if available, otherwise fall back to theme settings
    const searchTerms = this.shopifyPopularSearches || this.trendingSearches;
    
    return `
      ${this.recentSearches.length > 0 ? `
        <section class="search-redesign__section">
          <h3 class="search-redesign__section-title">RECENT SEARCHES</h3>
          <ul class="search-redesign__list">
            ${this.recentSearches.map(term => `
              <li class="search-redesign__item">
                <a href="#" class="search-redesign__link" data-search="${term}">${term}</a>
              </li>
            `).join('')}
          </ul>
        </section>
      ` : ''}
      
      <section class="search-redesign__section">
        <h3 class="search-redesign__section-title">TRENDING SEARCHES</h3>
        <ul class="search-redesign__list">
          ${searchTerms.map(term => `
            <li class="search-redesign__item">
              <a href="#" class="search-redesign__link" data-search="${term}">${term}</a>
            </li>
          `).join('')}
        </ul>
      </section>
    `;
  }
  
  async fetchPopularSearches() {
    this.popularSearchesFetched = true;
    
    try {
      // Try to get popular searches from Shopify (empty query)
      const response = await fetch(`${theme.routes.predictiveSearch}?q=&resources[type]=query&resources[limit]=10`);
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.resources?.results?.queries?.length > 0) {
          this.shopifyPopularSearches = data.resources.results.queries.map(q => 
            q.text || q.title || q
          ).filter(Boolean);
          
          // Re-render the panel with updated searches
          if (this.currentState === this.searchStates.INITIAL) {
            this.updatePanel();
          }
        }
      }
    } catch (error) {
      console.warn('Failed to fetch popular searches:', error);
    }
  }
  
  renderTypingPanel() {
    const query = this.input.value.trim();
    
    // Prioritize Shopify query suggestions if available
    let suggestionsHtml = '';
    
    if (this.shopifyQuerySuggestions && this.shopifyQuerySuggestions.length > 0) {
      // Use Shopify's styled text (includes ghost text highlighting)
      suggestionsHtml = this.shopifyQuerySuggestions.map(suggestion => `
        <li class="search-redesign__item">
          <a href="#" class="search-redesign__link" data-search="${suggestion.plainText}">${suggestion.styledText}</a>
        </li>
      `).join('');
    } else if (this.dynamicSuggestions && this.dynamicSuggestions.length > 0) {
      // Fall back to other dynamic suggestions
      suggestionsHtml = this.dynamicSuggestions.slice(0, 5).map(term => `
        <li class="search-redesign__item">
          <a href="#" class="search-redesign__link" data-search="${term}">${term}</a>
        </li>
      `).join('');
    } else {
      // Last resort: use basic suggestions
      const suggestions = this.getSuggestions(query);
      suggestionsHtml = suggestions.map(term => `
        <li class="search-redesign__item">
          <a href="#" class="search-redesign__link" data-search="${term}">${term}</a>
        </li>
      `).join('');
    }
    
    return `
      <section class="search-redesign__section">
        <h3 class="search-redesign__section-title">SUGGESTED</h3>
        <ul class="search-redesign__list">
          ${suggestionsHtml}
        </ul>
      </section>
      
      ${this.recentSearches.length > 0 ? `
        <section class="search-redesign__section">
          <h3 class="search-redesign__section-title">RECENT SEARCHES</h3>
          <ul class="search-redesign__list">
            ${this.recentSearches.slice(0, 3).map(term => `
              <li class="search-redesign__item">
                <a href="#" class="search-redesign__link" data-search="${term}">${term}</a>
              </li>
            `).join('')}
          </ul>
        </section>
      ` : ''}
    `;
  }
  
  renderResultsPanel() {
    const query = this.input.value.trim();
    
    console.log('Rendering results panel with:', {
      collections: this.searchCollections,
      pages: this.resultPages
    });
    
    // For results panel, prioritize Shopify query suggestions
    let suggestionsHtml = '';
    
    if (this.shopifyQuerySuggestions && this.shopifyQuerySuggestions.length > 0) {
      // Use Shopify's query suggestions (these are the Search & Discovery suggestions)
      suggestionsHtml = this.shopifyQuerySuggestions.map(suggestion => `
        <li class="search-redesign__item">
          <a href="#" class="search-redesign__link" data-search="${suggestion.plainText}">${suggestion.styledText}</a>
        </li>
      `).join('');
    } else {
      // Fall back to related searches
      const relatedSearches = this.getRelatedSearches(query);
      suggestionsHtml = relatedSearches.map(term => `
        <li class="search-redesign__item">
          <a href="#" class="search-redesign__link" data-search="${term}">${term}</a>
        </li>
      `).join('');
    }
    
    // Collections section
    let collectionsContent = '';
    if (this.searchCollections && this.searchCollections.length > 0) {
      collectionsContent = this.searchCollections.map(collection => `
        <li class="search-redesign__item">
          <a href="${collection.url || '#'}" class="search-redesign__link">${collection.title || 'Untitled'}</a>
        </li>
      `).join('');
    }
    
    // Pages section
    let pagesContent = '';
    if (this.resultPages && this.resultPages.length > 0) {
      pagesContent = this.resultPages.map(page => `
        <li class="search-redesign__item">
          <a href="${page.url}" class="search-redesign__link">${page.title}</a>
        </li>
      `).join('');
    }
    
    return `
      <section class="search-redesign__section">
        <h3 class="search-redesign__section-title">SUGGESTED</h3>
        <ul class="search-redesign__list">
          ${suggestionsHtml}
        </ul>
      </section>
      
      ${collectionsContent ? `
        <section class="search-redesign__section">
          <h3 class="search-redesign__section-title">COLLECTIONS</h3>
          <ul class="search-redesign__list">
            ${collectionsContent}
          </ul>
        </section>
      ` : ''}
      
      ${pagesContent ? `
        <section class="search-redesign__section">
          <h3 class="search-redesign__section-title">PAGES</h3>
          <ul class="search-redesign__list">
            ${pagesContent}
          </ul>
        </section>
      ` : ''}
    `;
  }
  
  bindPanelEvents() {
    // Bind click events to search links
    this.panel.querySelectorAll('[data-search]').forEach(link => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        const searchTerm = e.target.dataset.search;
        this.input.value = searchTerm;
        this.handleSubmit(e);
      });
    });
  }
  
  async performSearch(query, saveToRecent = false) {
    // Only add to recent searches when explicitly requested
    if (saveToRecent && query && query.trim()) {
      this.addToRecentSearches(query.trim());
    }
    
    // Cancel previous search
    if (this.searchAbortController) {
      this.searchAbortController.abort();
    }
    
    this.searchAbortController = new AbortController();
    
    try {
      // Show loading state
      this.results.innerHTML = '<div class="search-redesign__loading">Searching...</div>';
      
      // Clear previous search data
      this.searchCollections = [];
      this.resultPages = [];
      
      // Update state to results
      this.updateState(this.searchStates.RESULTS);
      
      // Check if we're on mobile (matches CSS breakpoint)
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      
      // Make API calls based on device
      console.log('Starting searches for:', query, 'Mobile:', isMobile);
      
      // 1. Always fetch products
      this.fetchProducts(query).then(searchData => {
        console.log('Products response received');
        if (searchData.productGrid && searchData.productGrid.children.length > 0) {
          const gridClasses = searchData.productGrid.className;
          const modifiedGrid = this.addSearchTrackingToProducts(searchData.productGrid.cloneNode(true), query);
          
          this.results.innerHTML = `
            <div class="search-redesign__header">
              ${searchData.resultCount ? `<p class="search-redesign__count">${searchData.resultCount.textContent}</p>` : ''}
            </div>
            <div class="collection-listing">
              <div class="${gridClasses}">
                ${modifiedGrid.innerHTML}
              </div>
            </div>
          `;
          
          if (window.theme?.initProductBlocks) {
            window.theme.initProductBlocks(this.results);
          }
        }
      }).catch(error => {
        console.error('Products fetch error:', error);
      });
      
      // Only fetch collections and pages on desktop
      if (!isMobile) {
        // 2. Fetch collections
        this.fetchCollections(query).then(() => {
          console.log('Collections updated, refreshing panel');
          this.updatePanel();
        }).catch(error => {
          console.error('Collections fetch error:', error);
        });
        
        // 3. Fetch pages and articles
        this.fetchPages(query).then(() => {
          console.log('Pages updated, refreshing panel');
          this.updatePanel();
        }).catch(error => {
          console.error('Pages fetch error:', error);
        });
      } else {
        console.log('Skipping collections and pages fetch on mobile');
      }
      
      // Fetch dynamic suggestions
      await this.fetchDynamicSuggestions(query);
      this.updatePanel();
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Search error:', error);
        this.results.innerHTML = '<div class="search-redesign__error">Search failed. Please try again.</div>';
      }
    }
  }
  
  async fetchCollections(query) {
    try {
      // Use the correct JSON endpoint
      const url = `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=collection&resources[limit]=10`;
      console.log('Fetching collections from:', url);
      
      const response = await fetch(url, {
        signal: this.searchAbortController.signal
      });
      
      console.log('Collections response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Collections API response:', data);
        
        if (data.resources?.results?.collections) {
          const collections = data.resources.results.collections;
          console.log('Collections found before filtering:', collections.length);
          console.log('Collection data:', collections);
          
          // First check if collections have product_count in the response
          const initialFiltered = collections.filter(collection => {
            // If product count is provided and is 0, filter out immediately
            if (collection.products_count === 0) {
              console.log(`Collection "${collection.title}" has 0 products (from API), filtering out`);
              return false;
            }
            return true;
          });
          
          // If we filtered some out, use the filtered list
          if (initialFiltered.length < collections.length) {
            this.searchCollections = initialFiltered.slice(0, 5);
            console.log('Collections after initial filtering:', this.searchCollections.length);
            this.updatePanel();
          } else {
            // Otherwise, do the deeper check
            this.filterCollectionsWithProducts(collections).then(filteredCollections => {
              this.searchCollections = filteredCollections;
              console.log('Collections after deep filtering:', this.searchCollections.length);
              // Update panel to show filtered collections
              this.updatePanel();
            });
          }
        } else {
          console.log('No collections in response structure:', data);
        }
        
        return data;
      } else {
        console.error('Collections API error:', response.status, response.statusText);
      }
    } catch (error) {
      console.warn('Failed to fetch collections:', error);
    }
    return null;
  }
  
  async filterCollectionsWithProducts(collections) {
    // Check each collection for VISIBLE products
    const collectionChecks = collections.map(async (collection) => {
      try {
        // Extract collection handle from URL
        const handle = collection.url.split('/').pop();
        
        // Fetch the actual collection page to check for visible products
        const collectionUrl = `${collection.url}`;
        const response = await fetch(collectionUrl);
        
        if (response.ok) {
          const html = await response.text();
          
          // Look for specific indicators of visible products
          // Check for product blocks with actual product data
          const hasVisibleProducts = 
            // Check for product blocks that aren't in a "no products" container
            (html.includes('class="product-block"') && !html.includes('no-products-message')) ||
            // Check for product grid with items
            (html.includes('product-grid') && html.includes('data-product-id=')) ||
            // Check that it's not showing the empty collection message
            (!html.includes('collection-empty') && !html.includes('no products found') && !html.includes('No products found'));
          
          // Also check if the page explicitly says there are no products
          const hasNoProductsMessage = 
            html.toLowerCase().includes('no products found') ||
            html.toLowerCase().includes('collection is empty') ||
            html.toLowerCase().includes('0 products') ||
            html.includes('class="collection-empty"');
          
          if (hasVisibleProducts && !hasNoProductsMessage) {
            console.log(`Collection "${collection.title}" has visible products`);
            return collection;
          } else {
            console.log(`Collection "${collection.title}" has no visible products, filtering out`);
            return null;
          }
        }
        
        // If we can't check, exclude by default
        console.log(`Could not verify collection "${collection.title}", excluding`);
        return null;
        
      } catch (error) {
        console.warn(`Failed to check collection ${collection.title}:`, error);
        // If check fails, exclude the collection
        return null;
      }
    });
    
    // Wait for all checks to complete
    const results = await Promise.all(collectionChecks);
    
    // Filter out null entries (collections without products) and limit to 5
    const filtered = results.filter(collection => collection !== null).slice(0, 5);
    console.log(`Filtered from ${collections.length} to ${filtered.length} collections with visible products`);
    
    return filtered;
  }
  
  async fetchPages(query) {
    try {
      // Use the correct JSON endpoint
      const url = `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=page,article&resources[limit]=5`;
      console.log('Fetching pages/articles from:', url);
      
      const response = await fetch(url, {
        signal: this.searchAbortController.signal
      });
      
      console.log('Pages response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Pages/Articles API response:', data);
        
        const pages = [];
        
        // Extract pages and articles from the correct structure
        if (data.resources?.results?.pages) {
          pages.push(...data.resources.results.pages);
          console.log('Pages found:', data.resources.results.pages.length);
        }
        
        if (data.resources?.results?.articles) {
          pages.push(...data.resources.results.articles);
          console.log('Articles found:', data.resources.results.articles.length);
        }
        
        if (pages.length > 0) {
          this.resultPages = pages;
          console.log('Total pages/articles stored:', this.resultPages.length, this.resultPages);
        } else {
          console.log('No pages/articles found in response');
        }
        
        return data;
      } else {
        console.error('Pages API error:', response.status, response.statusText);
      }
    } catch (error) {
      console.warn('Failed to fetch pages:', error);
    }
    return null;
  }
  
  async fetchProducts(query) {
    const searchUrl = new URL(theme.routes.base + '/search');
    searchUrl.searchParams.set('q', query);
    searchUrl.searchParams.set('type', 'product');
    searchUrl.searchParams.set('section_id', 'main-search');
    
    console.log('Fetching products from:', searchUrl.toString());
    
    const response = await fetch(searchUrl, {
      signal: this.searchAbortController.signal
    });
    
    console.log('Products response status:', response.status);
    
    if (!response.ok) throw new Error('Search failed');
    
    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    
    const productGrid = doc.querySelector('.collection-listing .product-grid');
    console.log('Products found:', productGrid ? productGrid.children.length : 0);
    
    return {
      productGrid: productGrid,
      resultCount: doc.querySelector('.utility-bar__centre .utility-bar__item')
    };
  }
  
  addSearchTrackingToProducts(productGrid, query) {
    // Add Shopify Search & Discovery tracking parameters to product links
    // Get all product blocks first, then find links within each
    const productBlocks = productGrid.querySelectorAll('.product-block');
    
    // Generate a search session ID if we don't have one
    if (!this.searchSessionId) {
      this.searchSessionId = this.generateSessionId();
    }
    
    productBlocks.forEach((block, index) => {
      // Find all product links within this block
      const links = block.querySelectorAll('a.product-link');
      
      // Add tracking to all links in this product block with the same position
      links.forEach(link => {
        const url = new URL(link.href);
        
        // Add search tracking parameters to match Shopify's format
        url.searchParams.set('_pos', index + 1); // Position in results (1-based)
        url.searchParams.set('_sid', this.searchSessionId); // Session ID
        url.searchParams.set('_ss', 'r'); // Search source (r = results page)
        
        // Only add _psq if it's not already in the URL
        if (!url.searchParams.has('_psq')) {
          url.searchParams.set('_psq', query); // Previous search query
        }
        
        link.href = url.toString();
      });
    });
    
    return productGrid;
  }
  
  generateSessionId() {
    // Generate a unique session ID similar to Shopify's format (8 chars)
    return Math.random().toString(36).substring(2, 10);
  }
  
  getSuggestions(query) {
    // Combine trending and recent searches
    const allTerms = [
      ...new Set([
        ...this.trendingSearches,
        ...this.recentSearches
      ])
    ];
    
    // Filter and sort by relevance
    const lowerQuery = query.toLowerCase();
    const suggestions = allTerms
      .filter(term => term.toLowerCase().includes(lowerQuery))
      .sort((a, b) => {
        // Prioritize terms that start with the query
        const aStarts = a.toLowerCase().startsWith(lowerQuery);
        const bStarts = b.toLowerCase().startsWith(lowerQuery);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;
        return a.length - b.length; // Then by length
      })
      .slice(0, 5);
    
    // If no suggestions, add the query itself with common suffixes
    if (suggestions.length === 0 && query.length > 2) {
      const commonSuffixes = ['s', ' tops', ' bottoms', ' sale'];
      return commonSuffixes
        .map(suffix => query + suffix)
        .filter(term => allTerms.some(t => t.toLowerCase().includes(term.toLowerCase())))
        .slice(0, 3);
    }
    
    return suggestions;
  }
  
  getRelatedSearches(query) {
    // Use dynamic suggestions if available, otherwise use predictive API data
    if (this.dynamicSuggestions && this.dynamicSuggestions.length > 0) {
      // Filter out the current query and return related suggestions
      return this.dynamicSuggestions
        .filter(suggestion => suggestion.toLowerCase() !== query.toLowerCase())
        .slice(0, 5);
    }
    
    // Fallback: combine trending and recent searches as related
    const allSuggestions = [...new Set([
      ...this.trendingSearches,
      ...this.recentSearches
    ])];
    
    // Filter out current query and return most relevant
    return allSuggestions
      .filter(term => term.toLowerCase() !== query.toLowerCase())
      .slice(0, 4);
  }
  
  getRecentSearches() {
    try {
      const searches = localStorage.getItem('recentSearches');
      const parsed = searches ? JSON.parse(searches) : [];
      
      // Filter out invalid entries
      const filtered = parsed.filter(term => {
        return term && 
               term.length >= 2 && 
               /[a-zA-Z0-9]/.test(term) &&
               typeof term === 'string';
      });
      
      // Update localStorage if we filtered anything out
      if (filtered.length !== parsed.length) {
        localStorage.setItem('recentSearches', JSON.stringify(filtered));
      }
      
      return filtered;
    } catch (e) {
      console.error('Failed to load recent searches:', e);
      return [];
    }
  }
  
  getTrendingSearches() {
    // Get trending searches from theme settings
    if (window.theme?.settings?.trendingSearches) {
      // Handle both string and array formats
      let searches = window.theme.settings.trendingSearches;
      
      if (typeof searches === 'string') {
        // Split by newlines and clean up
        searches = searches
          .split(/[\n\r]+/)
          .map(s => s.trim())
          .filter(s => s.length > 0);
      } else if (Array.isArray(searches)) {
        // Already an array, just clean up
        searches = searches
          .map(s => String(s).trim())
          .filter(s => s.length > 0);
      } else {
        // Invalid format, use defaults
        searches = [];
      }
      
      // Return searches or fallback to defaults if empty
      return searches.length > 0 ? searches : this.getDefaultTrendingSearches();
    }
    
    // Fallback defaults if settings not available
    return this.getDefaultTrendingSearches();
  }
  
  getDefaultTrendingSearches() {
    return [
      'leggings',
      'yoga pants', 
      'sports bras',
      'shorts',
      'new arrivals'
    ];
  }
  
  addToRecentSearches(term) {
    // Validate the search term
    if (!term || term.length < 2) return; // Skip very short terms
    
    // Skip if it's just punctuation or whitespace
    if (!/[a-zA-Z0-9]/.test(term)) return;
    
    // Clean the term
    term = term.trim();
    
    // Remove the term if it already exists
    this.recentSearches = this.recentSearches.filter(t => t !== term);
    // Add to the beginning
    this.recentSearches.unshift(term);
    // Keep only the last 5
    this.recentSearches = this.recentSearches.slice(0, 5);
    
    try {
      localStorage.setItem('recentSearches', JSON.stringify(this.recentSearches));
    } catch (e) {
      console.warn('Failed to save recent searches', e);
    }
  }
  
  updateResultsPages(doc) {
    // Extract actual page results if any
    const pageBlocks = doc.querySelectorAll('.product-block.page-block');
    const collectionBlocks = doc.querySelectorAll('.collection-block');
    
    this.resultPages = [];
    
    // Add pages from search results
    if (pageBlocks.length > 0) {
      pageBlocks.forEach(block => {
        const link = block.querySelector('a');
        if (link) {
          // Get title from link text or title element
          const titleElement = block.querySelector('.page-block__title');
          const title = titleElement ? titleElement.textContent.trim() : link.textContent.trim();
          
          this.resultPages.push({
            title: title,
            url: link.getAttribute('href') || link.href
          });
        }
      });
    }
    
    // Add collections if found
    if (collectionBlocks.length > 0) {
      collectionBlocks.forEach(block => {
        const link = block.querySelector('a');
        if (link) {
          // Get title from various possible elements
          const titleElement = block.querySelector('.collection-block__title, .collection-block__name, h3, h4');
          const title = titleElement ? titleElement.textContent.trim() : link.textContent.trim();
          
          this.resultPages.push({
            title: title,
            url: link.getAttribute('href') || link.href
          });
        }
      });
    }
    
    // Also check for pages in the full search response
    const searchResultsContainer = doc.querySelector('.collection-listing');
    if (searchResultsContainer) {
      // Look for any non-product links that might be pages
      const articleLinks = searchResultsContainer.querySelectorAll('a[href*="/pages/"], a[href*="/blogs/"]');
      articleLinks.forEach(link => {
        const title = link.textContent.trim();
        if (title && !this.resultPages.some(p => p.url === link.href)) {
          this.resultPages.push({
            title: title,
            url: link.getAttribute('href') || link.href
          });
        }
      });
    }
    
    // Limit to reasonable number
    this.resultPages = this.resultPages.slice(0, 5);
    
    // Update the panel
    this.updatePanel();
  }
  
  renderSearchIcon() {
    return `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M9 17A8 8 0 1 0 9 1a8 8 0 0 0 0 16zM15 15l4 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;
  }
  
  renderCloseIcon() {
    return `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M15 5L5 15M5 5l10 10" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;
  }
}

// Register the custom element
customElements.define('search-redesign', SearchRedesign);